/**
 * SentQuoteRightPanel — contractor-facing right panel shown on the sent-page
 * surface (`sent | lost | expired | archived`, same predicate that lazy-mounts
 * `SentQuoteView` at `QuoteDetail.js:726`).
 *
 * PR-56 (`347_ §4` · Wave B · B3 · 🟢 MEASURED).
 *
 * Two responsibilities:
 *   1. Name the T&C template ACTUALLY attached to this quote.
 *   2. List attachments as STATIC links (no add, no remove) — this is the
 *      contractor's read-only view of what the customer received.
 *
 * ACCEPT lines (dispatch 347_ §PR-56):
 *   • the panel names the T&C template that is actually attached to this quote
 *   • each attachment renders as a link, and no add/remove control is present
 *   • the five states are handled and ERROR is visually distinct from EMPTY
 *
 * FIVE STATES (canonical, per 000_BUILD_PRINCIPLES §3):
 *   LOADING · POPULATED · EMPTY · ERROR · NOT_PERMITTED
 * Each of the two sub-sections is a state machine; ERROR uses an amber tone
 * and EMPTY is muted-grey — visually distinct per the third ACCEPT line.
 *
 * Attachment shape handling: reads BOTH `quote.attachments[]` AND
 * `quote.documents[]` and merges, because the PT-53b write path lands refs
 * on `documents[]` (see `bidit/backend-go/cmd/quotes/attachments.go:225`
 * `appendRefToDocuments`) while legacy paths wrote to `attachments[]`.
 * Normalised through `entryDisplay` from `utils/attachmentEntry.js`.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '../ui';
import { entryDisplay, mergeDocumentEntries } from '../../utils/attachmentEntry';
import { loadTCTemplates } from '../../utils/loadTCTemplates';
import FilePreviewModal from '../attachments/FilePreviewModal';

/** Amber "ERROR" tone card. Kept as a local constant so the class list is
 * paired with the tone name in one place — swap the block, not the tone. */
const ERROR_TONE = 'border-amber-300 bg-amber-50';
const ERROR_TEXT = 'text-amber-800';
const MUTED_TEXT = 'text-xs text-gray-500';

/** Load state for the T&C template list. Named states, not booleans:
 * an implicit `!loading && !error && !data` triple has bitten us before
 * (§silent-success). */
const TEMPLATES_STATE = {
  IDLE: 'idle',           // no tcTemplateId on quote — no fetch needed
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
  FORBIDDEN: 'forbidden', // 403 from settings — vanishingly rare here
};

function TermsSection({ quote }) {
  const stampedId =
    quote?.tcTemplateId ||
    quote?.tc_template_id ||
    quote?.metadata?.tcTemplateId ||
    null;
  const stampedNameOnQuote =
    quote?.tcTemplateName ||
    quote?.tc_template_name ||
    quote?.metadata?.tcTemplateName ||
    null;
  const inlineTerms = quote?.termsConditions || quote?.terms_conditions || null;

  // Only fetch templates when we have a stamped id AND we don't already
  // carry a name for it. If the writer put a name in metadata, trust it —
  // avoids a redundant fetch and avoids a false ERROR when a template has
  // since been deleted from settings.
  const needsResolve = !!stampedId && !stampedNameOnQuote;

  const [state, setState] = useState(needsResolve ? TEMPLATES_STATE.LOADING : TEMPLATES_STATE.IDLE);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!needsResolve) return;
    let cancelled = false;
    setState(TEMPLATES_STATE.LOADING);
    (async () => {
      try {
        const list = await loadTCTemplates();
        if (cancelled) return;
        setTemplates(Array.isArray(list) ? list : []);
        setState(TEMPLATES_STATE.READY);
      } catch (err) {
        if (cancelled) return;
        // Best-effort 403 detection — `apiClient` throws with a `.status` on
        // ApiError, but loadTCTemplates already swallows to []. So this
        // branch is defensive: today loadTCTemplates never rejects.
        const status = err?.status || err?.response?.status;
        setState(status === 403 ? TEMPLATES_STATE.FORBIDDEN : TEMPLATES_STATE.ERROR);
      }
    })();
    return () => { cancelled = true; };
  }, [needsResolve, stampedId]);

  // Derive the display state.
  const resolved = useMemo(() => {
    if (!stampedId) return null;
    if (stampedNameOnQuote) return { id: stampedId, name: stampedNameOnQuote };
    if (state !== TEMPLATES_STATE.READY) return null;
    const hit = templates.find(t => t && (t.id === stampedId || String(t.id) === String(stampedId)));
    return hit ? { id: stampedId, name: hit.name || hit.title || 'Untitled template' } : null;
  }, [stampedId, stampedNameOnQuote, state, templates]);

  // --- render the five states ---

  // NOT_PERMITTED
  if (state === TEMPLATES_STATE.FORBIDDEN) {
    return (
      <div className={`rounded border p-3 ${ERROR_TONE}`}>
        <p className={`text-xs font-semibold ${ERROR_TEXT}`}>Terms &amp; Conditions</p>
        <p className={`text-xs ${ERROR_TEXT}`}>You do not have permission to view the terms template.</p>
      </div>
    );
  }

  // LOADING
  if (state === TEMPLATES_STATE.LOADING) {
    return (
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Terms &amp; Conditions</p>
        <div className="animate-pulse h-4 w-40 bg-gray-100 rounded" data-testid="sq-terms-loading" />
      </div>
    );
  }

  // ERROR — a template id is stamped on the quote but we could not resolve
  // it, either because the fetch failed OR the id no longer exists in
  // settings (deleted after send). Distinct from EMPTY per ACCEPT line 3.
  if (stampedId && !resolved && state !== TEMPLATES_STATE.LOADING) {
    // If the templates fetch succeeded but the id is missing from the list,
    // this is a "template deleted after send" case. If the fetch failed
    // outright, same visual — we cannot tell the customer which one.
    return (
      <div className={`rounded border p-3 ${ERROR_TONE}`}>
        <p className={`text-xs font-semibold ${ERROR_TEXT}`}>Terms &amp; Conditions</p>
        <p className={`text-xs ${ERROR_TEXT}`}>
          Template <code className="font-mono">{stampedId}</code> — could not resolve.
        </p>
      </div>
    );
  }

  // POPULATED — either resolved from list, or a name was on the quote,
  // or (legacy) an inline terms body was pasted at send time.
  if (resolved) {
    return (
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Terms &amp; Conditions</p>
        <p className="text-sm text-gray-800">{resolved.name}</p>
      </div>
    );
  }
  if (inlineTerms) {
    return (
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Terms &amp; Conditions</p>
        <p className="text-sm text-gray-800">Custom terms (inline)</p>
      </div>
    );
  }

  // EMPTY
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Terms &amp; Conditions</p>
      <p className={MUTED_TEXT}>No terms template attached.</p>
    </div>
  );
}

function AttachmentsSection({ quote }) {
  // Merge attachments[] and documents[] (backend PT-53b writes refs to
  // documents[]; legacy writes landed on attachments[]). Dedupe on the
  // normalised key so we don't render the same entry twice if it exists
  // in both arrays.
  //
  // 🔍 Dedupe key (§353 measurement, tenant 270): live docs are Ref-shape
  // `{ attachmentId, filename, sizeBytes, contentType, attachedAt, source }`
  // — they carry NO raw `key` field. `entryDisplay.key` normalises
  // `attachmentId` → `key`, but corrupt/unknown entries all collapse to
  // the literal string 'unknown' in that path, and two such entries would
  // silently merge into one row. Previous code also used
  // `Math.random().toString(36)` as a last fallback, which is worse: it
  // guarantees non-collision at the cost of never deduping legitimate
  // duplicates and defeating render memoization.
  //
  // Explicit precedence, live-data-first:
  //   1. `attachmentId`     — canonical for Ref-shape (Portica-measured live)
  //   2. `id`               — canonical for Legacy-shape
  //   3. `filename + sizeBytes` — for entries missing both ids but
  //                            uniquely identifiable by content-fingerprint
  //   4. `array-index`      — a stable per-render fallback that keeps two
  //                            un-identifiable entries as separate rows
  //                            (the load-bearing property of this fix)
  //
  // ACCEPT (§353 §3.1):
  //   • two docs with different attachmentIds both render
  //   • the same doc present in BOTH arrays renders once
  //   • two entries with no identifying data render as TWO rows (not one)
  const merged = useMemo(() => {
    const raw = mergeDocumentEntries(quote);
    const seen = new Set();
    const out = [];
    raw.forEach((e, idx) => {
      const d = entryDisplay(e);
      const rawEntry = e || {};
      const attachmentId = rawEntry.attachmentId || d.raw?.attachmentId || null;
      const id = rawEntry.id || d.raw?.id || null;
      const filename = rawEntry.filename || rawEntry.name || d.name || null;
      const sizeBytes = rawEntry.sizeBytes ?? rawEntry.size ?? null;

      let dedupeKey;
      if (attachmentId) {
        dedupeKey = `att:${attachmentId}`;
      } else if (id) {
        dedupeKey = `id:${id}`;
      } else if (filename && sizeBytes != null) {
        dedupeKey = `fp:${filename}::${sizeBytes}`;
      } else {
        // No identifying data. DO NOT collapse into a shared 'unknown' key.
        // Position-based key keeps distinct entries as distinct rows; the
        // `hasBrokenEntry` check below still renders the amber banner
        // above the list so this state is visible, not silent.
        dedupeKey = `pos:${idx}`;
      }
      // mergeDocumentEntries already deduped by attachmentId/id. This
      // second pass adds display-level deduplication for entries that have
      // no attachmentId/id (mergeDocumentEntries keeps ALL of those per
      // its identity contract). The fingerprint and position-based keys
      // handle the edge cases: identical-content id-less entries stay as
      // separate rows (position-based), while entries with the same
      // filename+size but different positions are collapsed (fingerprint).
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      out.push({ ...d, dedupeKey });
    });
    return out;
  }, [quote?.attachments, quote?.documents]); // eslint-disable-line react-hooks/exhaustive-deps

  // ERROR detection: `entryDisplay` returns `shape: 'unknown'` and
  // `name: 'Corrupt attachment entry'` for un-classifiable entries. Also
  // flag entries that lack a usable name AND lack a link — those render
  // as an unnamed non-functional row, which is worse than a visible error.
  const hasBrokenEntry = merged.some(d =>
    d.shape === 'unknown' ||
    ((!d.name || d.name === 'Untitled' || d.name === 'Corrupt attachment entry') && !d.openHref && d.shape !== 'attachment')
  );

  // EMPTY
  if (merged.length === 0) {
    return (
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Attachments</p>
        <p className={MUTED_TEXT}>No attachments.</p>
      </div>
    );
  }

  // POPULATED (with possible ERROR banner above the list)
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Attachments</p>
      {hasBrokenEntry && (
        <div className={`rounded border p-2 mb-2 ${ERROR_TONE}`}>
          <p className={`text-xs ${ERROR_TEXT}`}>One or more attachments have incomplete metadata.</p>
        </div>
      )}
      <ul className="space-y-1" data-testid="sq-attachments-list">
        {merged.map((d) => {
          // Each attachment renders AS A LINK per the second ACCEPT. Two
          // shapes need distinct link wiring:
          //   • Legacy (Shape A): a direct `openHref` — usable <a href>.
          //   • Ref (Shape B): `openHref` is null; we render an anchor
          //     whose click opens the preview modal (IDI-445), which
          //     resolves the file by attachment id. Still an <a> element.
          // `entryDisplay` normalises the filename to `.name` (see
          // attachmentEntry.js:132) — legacy `filename` reads through it.
          const displayName =
            d.name && d.name !== 'Corrupt attachment entry' && d.name !== 'Untitled'
              ? d.name
              : 'Attachment';
          if (d.openHref) {
            return (
              <li key={d.dedupeKey}>
                <a
                  href={d.openHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:text-primary-800 hover:underline break-all"
                >
                  {displayName}
                </a>
              </li>
            );
          }
          // Ref-shape entry. Opens the preview modal on click.
          return (
            <li key={d.dedupeKey}>
              <RefLink entry={d} displayName={displayName} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Ref-shape link (IDI-445): renders as an anchor; a click opens the shared
 * preview modal, which resolves the file by attachment id. Read-only on the
 * sent quote (F1): no Delete, no add, no re-upload. */
function RefLink({ entry, displayName }) {
  const [open, setOpen] = useState(false);

  function openIt(e) {
    e.preventDefault();
    setOpen(true);
  }

  const label = displayName || entry?.name || 'Attachment';
  return (
    <>
      {/* ACCEPT requires attachments to render AS LINKS. For Ref-shape entries
          the URL isn't known until the modal resolves it, so there's no static
          href to embed. jsx-a11y suggests a <button> here — that would break
          "renders as a link" and diverge visually from the Legacy-shape
          anchors above. Keyboard access is preserved via anchor tabbability. */}
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        href="#"
        onClick={openIt}
        className="text-sm break-all text-primary-600 hover:text-primary-800 hover:underline"
      >
        {label}
      </a>
      {open && <FilePreviewModal entry={entry?.raw} onClose={() => setOpen(false)} />}
    </>
  );
}

/** The panel wrapper — a single Card in the sidebar. Only rendered when
 * `QuoteDetail` decides this quote is on the sent-page surface. */
export default function SentQuoteRightPanel({ quote }) {
  if (!quote) return null;
  return (
    <Card className="p-4 space-y-4" data-testid="sent-right-panel">
      <TermsSection quote={quote} />
      <AttachmentsSection quote={quote} />
    </Card>
  );
}
